import { TFunction } from 'i18next';

const errorFields = (error: unknown): Record<string, unknown> | undefined => {
  const data = error instanceof Object && 'data' in error ? error.data : undefined;
  return data instanceof Object ? (data as Record<string, unknown>) : undefined;
};

const mentions = (messages: unknown, text: string): boolean =>
  [messages].flat().some((message) => typeof message === 'string' && message.includes(text));

/**
 * Whether the API refused the name as a duplicate.
 *
 * The one error arm worth telling apart: `name` is unique per organisation, and
 * "that name is taken" is something the user can act on where a bare failure is
 * not. The serializer's own `validate` answers on `name`; the model constraint's
 * `non_field_errors` wording is the shape the API sent before it.
 */
export const isDuplicateName = (error: unknown): boolean => {
  const fields = errorFields(error);
  if (fields === undefined) return false;

  return (
    mentions(fields['name'], 'A project with this name already exists') ||
    mentions(fields['non_field_errors'], 'The fields name, organization_id must make a unique set.')
  );
};

/**
 * The email of whoever created the project holding the refused name, when the
 * API knows it. A user who cannot see that project can ask them for access.
 */
export const existingProjectCreator = (error: unknown): string | undefined => {
  const [email] = [errorFields(error)?.['existing_project_created_by']].flat();

  return typeof email === 'string' && email !== '' ? email : undefined;
};

/** The message for a refused duplicate name, naming the existing project's creator when known. */
export const duplicateNameMessage = (error: unknown, t: TFunction): string => {
  const creator = existingProjectCreator(error);

  return creator === undefined
    ? t('project-creation.duplicate-project-name')
    : t('project-creation.duplicate-project-name-contact-creator', { email: creator });
};
